/*!
 * Copyright © 2023. Verizon Connect Ireland Limited. All rights reserved.
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { IMapHierarchyItem, IPlaceCategory, IReverseGeocodeResponse } from '../../../core/models';
import { PlacesHttpService } from '../../../core/services';
import { EGeofenceFormTabs, EPlaceType, IGeofenceFormData, IPlace } from '../../models';
import {
  GeofenceClientIdValidator,
  GeofenceMandatoryGroupsValidator,
  GeofencePhoneNumberValidator,
  WhitespaceValidator
} from '../../validators';

@Component({
  selector: 'fm-geofence-form',
  templateUrl: './geofence-form.component.html',
  styleUrls: ['./geofence-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeofenceFormComponent implements OnChanges, OnDestroy {
  @Input()
  public geoFenceAddress: IReverseGeocodeResponse;
  @Input()
  public hierarchyGroups: IMapHierarchyItem[];
  @Input()
  public placeCategories: IPlaceCategory[];
  @Input()
  public mandatoryGeofenceGroupsEnabled: boolean;
  @Input()
  public geoFenceForm: IPlace;

  @Output()
  public saveGeofence = new EventEmitter<IGeofenceFormData>();
  @Output()
  public closeGeofencePanel = new EventEmitter<void>();

  @ViewChild('saveModal') saveModalTemplate: TemplateRef<any>;
  @ViewChild('cancelModal') cancelModalTemplate: TemplateRef<any>;

  public detailsFormGroup: UntypedFormGroup;
  public advancedOptionsContentFormGroup: UntypedFormGroup;

  public isEditMode: boolean = false;
  public isHotSpot: boolean = false;

  public EGeofenceFormTabs = EGeofenceFormTabs;
  public activeTab: EGeofenceFormTabs = EGeofenceFormTabs.Details;

  public geofenceHelpCenterUrl =
    'https://reveal-help.verizonconnect.com/hc/en-us/articles/360010453700-Alert-Types#h_fc1eff04-6c7a-4d32-965b-94ddac253a59';

  private readonly _unsubscribe$ = new Subject<void>();

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _placesHttpService: PlacesHttpService,
    private readonly _dialog: MatLegacyDialog
  ) {
    this.detailsFormGroup = this._formBuilder.group({
      address: ['', Validators.required],
      name: this._formBuilder.control('', {
        validators: [Validators.required, WhitespaceValidator.createValidator()]
      }),
      groups: '',
      category: '',
      description: ''
    });

    this.advancedOptionsContentFormGroup = this._formBuilder.group({
      contactName: '',
      contactPhoneNumber: this._formBuilder.control('', {
        updateOn: 'blur',
        validators: GeofencePhoneNumberValidator.createValidator()
      }),
      clientID: this._formBuilder.control('', {
        updateOn: 'blur',
        asyncValidators: GeofenceClientIdValidator.createValidator(this._placesHttpService, '')
      }),
      showOnMap: true,
      showOnAddress: true
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['geoFenceAddress']) {
      this.detailsFormGroup.get('address')?.setValue(this.geoFenceAddress?.fulladdress ?? '');
    }
    if (changes['mandatoryGeofenceGroupsEnabled'] && changes['mandatoryGeofenceGroupsEnabled'].currentValue === true) {
      this.detailsFormGroup.get('groups')?.setValidators([GeofenceMandatoryGroupsValidator.createValidator(), Validators.required]);
    }
    if (changes['geoFenceForm']) {
      this.isHotSpot = this.geoFenceForm?.type === EPlaceType.HotSpot;

      const selectedGroups = this.geoFenceForm?.groupids.split(',').map(groupId => 'g' + groupId) ?? [];
      this.detailsFormGroup.get('name')?.setValue(!this.isHotSpot && this.geoFenceForm?.name ? this.geoFenceForm?.name : '');
      this.detailsFormGroup.get('description')?.setValue(this.geoFenceForm?.desc ?? '');
      this.detailsFormGroup.get('category')?.setValue(this.geoFenceForm?.catid ?? '');
      this.detailsFormGroup.get('groups')?.setValue(this.hierarchyGroups?.filter(group => selectedGroups.includes(group.id)) ?? '');

      this.advancedOptionsContentFormGroup.get('contactName')?.setValue(this.geoFenceForm?.cname ?? '');
      this.advancedOptionsContentFormGroup.get('contactPhoneNumber')?.setValue(this.geoFenceForm?.cno ?? '');

      this.advancedOptionsContentFormGroup
        .get('clientID')
        ?.setAsyncValidators(GeofenceClientIdValidator.createValidator(this._placesHttpService, this.geoFenceForm?.clientid ?? ''));

      this.advancedOptionsContentFormGroup.get('clientID')?.setValue(this.geoFenceForm?.clientid ?? '');
      this.advancedOptionsContentFormGroup.get('showOnMap')?.setValue(this.geoFenceForm?.smap ?? true);
      this.advancedOptionsContentFormGroup.get('showOnAddress')?.setValue(this.geoFenceForm?.saddr ?? true);
      this.isEditMode = (this.geoFenceForm?.id ?? -1) > 0 && this.geoFenceForm.type === EPlaceType.GeoFence;
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public onCancelClick(): void {
    const dialogRef = this._dialog.open(this.cancelModalTemplate, {
      panelClass: 'geofence-dialog'
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribe$), take(1))
      .subscribe(() => {
        this.closeCancelModal(false);
      });
  }

  public closeSaveModal(): void {
    this._dialog.closeAll();
    this.closeGeofencePanel.emit();
  }

  public closeCancelModal(confirmCancel: boolean) {
    this._dialog.closeAll();
    if (confirmCancel) {
      this.closeGeofencePanel.emit();
    }
  }

  public onSaveClick(): void {
    const groups =
      !this.detailsFormGroup.value?.groups || this.detailsFormGroup.value?.groups === '' ? [] : this.detailsFormGroup.value?.groups;
    const category =
      this.placeCategories.find(category => category.Id === this.detailsFormGroup.value?.category) ??
      this.placeCategories.find(category => category.ImageCssClass === 'Uncategorized');
    const geofenceFormData: IGeofenceFormData = {
      id: this.geoFenceForm?.id ?? -1,
      address: this.geoFenceAddress,
      name: this.detailsFormGroup.value?.name.trim(),
      groups: groups,
      category: category,
      description: (this.detailsFormGroup.value?.description ?? '').trim(),
      contactName: (this.advancedOptionsContentFormGroup.value?.contactName ?? '').trim(),
      contactPhoneNumber: (this.advancedOptionsContentFormGroup.value?.contactPhoneNumber ?? '').trim(),
      clientID: (this.advancedOptionsContentFormGroup.value?.clientID ?? '').trim(),
      showOnMap: this.advancedOptionsContentFormGroup.value?.showOnMap,
      showOnAddress: this.advancedOptionsContentFormGroup.value?.showOnAddress,
      placeType: this.geoFenceForm?.type ?? EPlaceType.GeoFence
    };
    this.saveGeofence.emit(geofenceFormData);
    const dialogRef = this._dialog.open(this.saveModalTemplate, { panelClass: 'geofence-dialog' });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribe$), take(1))
      .subscribe(() => {
        this.closeSaveModal();
      });
  }
}
